import { forEach } from 'lodash';
import { useStaticQuery, graphql } from "gatsby";

export const parseACF = (queryData, nodeName, debug) => {    
    if (queryData && queryData[nodeName] ) {
        const nodes = queryData[nodeName].nodes;
        const acfJSON = nodes && nodes[0].acf_json

        if (!acfJSON) return;
        
        // Console log out if debug = true

        debug && console.log(JSON.parse(acfJSON))

        // Return parsed ACF JSON

        return {
            ...nodes[0],
            ...JSON.parse(acfJSON)
        }
    }
}

// Group option page keys by prefix

export const groupByPrefix = (data, prefix) => {
    let items = {}

    forEach(data, (item, key) => {
        if (key.includes(prefix)) {
            items[key.replace(`${prefix}_`, '')] = item;
        }
    })

    return items
}

// Get global data

export const getGlobalData = (prefix) => {
    const query = graphql`
        query {
            allWordpressGlobal {
                nodes {
                    acf_json
                }
            }
        }
    `

    // Fetch global data

    const globalData = parseACF(useStaticQuery(query), 'allWordpressGlobal')

    // Return global + grouped by prefix

    return {
        global: globalData,
        data: groupByPrefix(globalData, prefix)
    }
}

// Scroll to ref

export const scrollToRef = (ref) => {
    if (!ref || !ref.current) return;

    try {
        window.scroll({
            top: ref.current.offsetTop,
            left: 0,
            behavior: 'smooth',
        });
    } catch (error) {
        window.scrollTo(ref.current.offsetTop, 0);
    }
}
 
export const scrollToTop = () => {
    try {
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    } catch (error) {
        window.scrollTo(0, 0);
    }
}

/**
 * Optimize Vimeo URL for faster loading
 * @param {string} vimeoUrl - Original Vimeo URL
 * @param {boolean} isMobile - Whether the device is mobile
 * @returns {string} Optimized Vimeo URL
 */
export const optimizeVimeoUrl = (vimeoUrl, isMobile = false) => {
	if (!vimeoUrl || typeof vimeoUrl !== 'string') return '';
	
	// If it's already a properly formatted URL, return as-is to avoid breaking it
	if (vimeoUrl.includes('player.vimeo.com') || vimeoUrl.includes('?')) {
		return vimeoUrl;
	}
	
	// Extract video ID from various Vimeo URL formats
	const videoIdMatch = vimeoUrl.match(/(?:vimeo\.com\/)(?:.*\/)?(\d+)/);
	if (!videoIdMatch || !videoIdMatch[1]) {
		console.warn('Could not extract video ID from:', vimeoUrl);
		return vimeoUrl; // Return original URL if we can't parse it
	}
	
	const videoId = videoIdMatch[1];
	
	// Validate video ID (should be numeric and reasonable length)
	if (!/^\d{6,12}$/.test(videoId)) {
		console.warn('Invalid video ID format:', videoId);
		return vimeoUrl;
	}
	
	// Use Vimeo player embed URL for better performance and compatibility
	const baseUrl = `https://player.vimeo.com/video/${videoId}`;
	
	// Add performance-focused parameters
	const params = new URLSearchParams({
		// Essential parameters for background videos
		autoplay: '1',
		muted: '1',
		loop: '1',
		// Remove unnecessary elements for faster loading
		byline: '0',
		portrait: '0',
		title: '0',
		// Performance optimizations
		dnt: '1', // Do not track
		pip: '0', // Disable picture-in-picture
		controls: '0' // Remove controls for background videos
	});
	
	// Add quality parameter only if mobile
	if (isMobile) {
		params.set('quality', '540p');
	}
	
	const optimizedUrl = `${baseUrl}?${params.toString()}`;
	return optimizedUrl;
};

/**
 * Create video preload hints for better performance
 * @param {string} videoUrl - Video URL to preload
 */
export const createVideoPreloadHint = (videoUrl) => {
	if (typeof window === 'undefined' || !videoUrl) return null;
	
	try {
		// Create preload link
		const preloadLink = document.createElement('link');
		preloadLink.rel = 'preload';
		preloadLink.as = 'video';
		preloadLink.href = videoUrl;
		preloadLink.crossOrigin = 'anonymous';
		
		// Add to head if not already present
		const existingPreload = document.querySelector(`link[href="${videoUrl}"]`);
		if (!existingPreload) {
			document.head.appendChild(preloadLink);
		}
		
		return preloadLink;
	} catch (error) {
		console.error('Error creating video preload hint:', error);
		return null;
	}
};

/**
 * Lazy load video when it's about to enter viewport
 * @param {HTMLElement} element - Element to observe
 * @param {Function} callback - Callback when element enters viewport
 */
export const createVideoLazyLoader = (element, callback) => {
	if (typeof window === 'undefined' || !element || typeof callback !== 'function') return null;
	
	try {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						callback();
						observer.unobserve(entry.target);
					}
				});
			},
			{
				rootMargin: '50px' // Start loading 50px before entering viewport
			}
		);
		
		observer.observe(element);
		return observer;
	} catch (error) {
		console.error('Error creating video lazy loader:', error);
		return null;
	}
};

/**
 * Get optimal video configuration for ReactPlayer
 * @param {boolean} isMobile - Whether the device is mobile
 * @param {boolean} isBackground - Whether this is a background video
 */
export const getOptimalVideoConfig = (isMobile = false, isBackground = true) => {
	try {
		return {
			vimeo: {
				playerOptions: {
					quality: isMobile ? '540p' : 'auto',
					speed: false,
					pip: false,
					byline: false,
					portrait: false,
					title: false,
					transparent: false,
					responsive: true,
					dnt: true, // Do not track for privacy/performance
					controls: !isBackground,
					autoplay: isBackground,
					muted: isBackground,
					loop: isBackground,
					background: isBackground ? 1 : 0
				}
			}
		};
	} catch (error) {
		console.error('Error creating video config:', error);
		// Return minimal config as fallback
		return {
			vimeo: {
				playerOptions: {
					muted: true,
					autoplay: true,
					loop: true
				}
			}
		};
	}
};


